import React, { useState } from 'react';
import { Box, Grid, Alert, Button } from '@mui/material';
import TeamNotificationsForm from 'team/components/adminNotifications/TeamNotificationsForm';
import TeamNotificationsPreview from 'team/components/adminNotifications/TeamNotificationsPreview';
import { useTeam } from 'team/contexts/teamContext';
import { sendTeamEmailNotification, sendTeamPushNotification } from 'team/apis/teamApi';
import { requestNotificationPermission, registerFcmTokenIfPermissionGranted } from 'team/utils/pushNotificationUtils';

const TeamAdminNotificationsContainer = () => {
    const { currentTeamId } = useTeam();

    // Auto-register FCM token when admin visits Notifications page with permission already granted (e.g. from address bar)
    React.useEffect(() => {
        if (currentTeamId && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            registerFcmTokenIfPermissionGranted(currentTeamId);
        }
    }, [currentTeamId]);

    const [notificationType, setNotificationType] = useState('email'); // 'push' or 'email'
    const [recipients, setRecipients] = useState('all');
    const [recipientFilters, setRecipientFilters] = useState({ subteams: [], members: [] });
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().split('T')[0]);
    const [scheduleTime, setScheduleTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showRegisterButton, setShowRegisterButton] = useState(false);
    const [registering, setRegistering] = useState(false);

    const handleRecipientFiltersChange = (filters, recipientUserIds) => {
        setRecipientFilters(filters);
        setRecipients(recipientUserIds.length > 0 ? recipientUserIds.join(',') : 'all');
    };

    const handleNotificationTypeChange = async (type) => {
        setNotificationType(type);
        if (type === 'push' && currentTeamId) {
            const result = await requestNotificationPermission(currentTeamId);
            if (!result.success && result.reason === 'denied') {
                setErrorMessage('Notifications were blocked. To receive push notifications, allow them in your browser settings (click the lock/info icon in the address bar → Site settings → Notifications).');
            } else if (!result.success && result.reason === 'unsupported') {
                setErrorMessage('Push notifications are not supported in this browser.');
            }
        }
    };

    const handleSend = async () => {
        if (!subject || !message) {
            setErrorMessage('Subject and Message are required.');
            return;
        }

        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');
        setShowRegisterButton(false);

        try {
            if (notificationType === 'push' && currentTeamId) {
                const permission = typeof Notification !== 'undefined' ? Notification.permission : null;
                const result = permission === 'granted'
                    ? await registerFcmTokenIfPermissionGranted(currentTeamId)
                    : await requestNotificationPermission(currentTeamId);
                if (!result.success && result.reason === 'denied') {
                    setLoading(false);
                    setErrorMessage('Notifications are blocked. Enable them in your browser settings (address bar → lock icon → Site settings → Notifications) to send and receive push notifications.');
                    return;
                }
                if (!result.success && result.reason === 'unsupported') {
                    setLoading(false);
                    setErrorMessage('Push notifications are not supported in this browser.');
                    return;
                }
                if (!result.success && (result.reason === 'error' || result.reason === 'no-token')) {
                    setLoading(false);
                    setErrorMessage(result.errorMessage || 'Could not register this device for push. Check browser console for details.');
                    setShowRegisterButton(true);
                    return;
                }
            }

            const data = {
                notificationType,
                recipients: recipients || 'all',
                subject,
                message,
                scheduleDate: scheduleEnabled ? scheduleDate : null,
                scheduleTime: scheduleEnabled ? scheduleTime : null
            };

            let response;
            if (notificationType === 'email') {
                response = await sendTeamEmailNotification(currentTeamId, data);
            } else {
                response = await sendTeamPushNotification(currentTeamId, data);
            }

            if (response && response.success) {
                setSuccessMessage(response.message || 'Notification sent successfully');
                setSubject('');
                setMessage('');
            } else {
                setErrorMessage(response?.message || 'Failed to send notification');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'An error occurred while sending notification';
            setErrorMessage(msg);
            if (notificationType === 'push' && typeof msg === 'string' && msg.toLowerCase().includes('no valid device tokens')) {
                setShowRegisterButton(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterDevice = async () => {
        if (!currentTeamId) return;
        setRegistering(true);
        setErrorMessage('');
        const result = typeof Notification !== 'undefined' && Notification.permission === 'granted'
            ? await registerFcmTokenIfPermissionGranted(currentTeamId)
            : await requestNotificationPermission(currentTeamId);
        setRegistering(false);
        if (result.success) {
            setSuccessMessage('Device registered. You can now send the push notification again.');
            setShowRegisterButton(false);
        } else if (result.reason === 'denied') {
            setErrorMessage('Notifications are blocked. Enable them in your browser (lock icon → Site settings → Notifications).');
        } else {
            setErrorMessage(result.errorMessage || 'Registration failed. Check browser console (F12) for details.');
        }
    };

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
            {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }} action={
                    showRegisterButton && (
                        <Button color="inherit" size="small" onClick={handleRegisterDevice} disabled={registering}>
                            {registering ? 'Registering...' : 'Register this device'}
                        </Button>
                    )
                }>
                    {errorMessage}
                </Alert>
            )}

            <Grid container spacing={4} sx={{ height: '100%' }}>
                {/* Left Panel - Form */}
                <Grid item xs={12} md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                    <TeamNotificationsForm
                        notificationType={notificationType}
                        setNotificationType={handleNotificationTypeChange}
                        recipients={recipients}
                        recipientFilters={recipientFilters}
                        onRecipientFiltersChange={handleRecipientFiltersChange}
                        subject={subject}
                        setSubject={setSubject}
                        message={message}
                        setMessage={setMessage}
                        scheduleEnabled={scheduleEnabled}
                        setScheduleEnabled={setScheduleEnabled}
                        scheduleDate={scheduleDate}
                        setScheduleDate={setScheduleDate}
                        scheduleTime={scheduleTime}
                        setScheduleTime={setScheduleTime}
                        onSend={handleSend}
                        loading={loading}
                    />
                </Grid>

                {/* Right Panel - Preview */}
                <Grid item xs={12} md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                    <TeamNotificationsPreview
                        notificationType={notificationType}
                        subject={subject}
                        message={message}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default TeamAdminNotificationsContainer;
